import { consumeAuthSessionEndMessage } from '@/shared/api/authRefreshError';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';
import { PasswordInput } from '@/shared/components/ui/password-input';
import { Stack } from '@/shared/components/ui/row';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { isLoginAccountLockedError } from '@/shared/utils/loginAccountLock';
import { getLoginGeolocation } from '@/shared/utils/loginGeolocation';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useAuthStore } from '@/stores/authStore';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { Lock, Mail } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { LoginAccountLockedAlert } from './LoginAccountLockedAlert';

const loginSchema = z.object({
  email: z
    .string()
    .min(1, '이메일을 입력해주세요!')
    .email('올바른 이메일 형식이 아닙니다'),
  password: z.string().min(1, '비밀번호를 입력해주세요!'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export const LoginPage: React.FC = () => {
  const toast = useToast();
  const { login, isLoading, isAuthenticated, isInitialized, initialize, user } =
    useAuthStore();
  const navigate = useNavigate();
  /** API 잠금 응답 수신 시 — Figma 빨간 박스·입력 에러 톤 */
  const [accountLocked, setAccountLocked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const email = form.watch('email');
  const password = form.watch('password');

  useEffect(() => {
    if (!isInitialized) initialize();
  }, [isInitialized, initialize]);

  useEffect(() => {
    const message = consumeAuthSessionEndMessage();
    if (message) toast.error(message);
  }, [toast]);

  useEffect(() => {
    if (!isInitialized || !isAuthenticated) return;
    if (user?.temporaryPasswordExpired) {
      toast.warning(
        '임시 비밀번호가 만료되었습니다. 관리자에게 재발급을 요청해주세요.',
      );
      void useAuthStore.getState().logout();
      return;
    }
    if (user?.mustChangePassword) {
      navigate({ to: ROUTE_PATHS.FORCE_CHANGE_PASSWORD } as never);
      return;
    }
    navigate({ to: ROUTE_PATHS.DASHBOARD } as never);
  }, [
    isInitialized,
    isAuthenticated,
    user?.mustChangePassword,
    user?.temporaryPasswordExpired,
    navigate,
    toast,
  ]);

  useEffect(() => {
    setAccountLocked(false);
  }, [email, password]);

  const onSubmit = async (values: LoginFormValues) => {
    setIsSubmitting(true);
    try {
      // 위치 실패가 로그인을 막으면 안 된다 — 거부·타임아웃이면 null
      const geolocation = await getLoginGeolocation();
      await login(values.email.trim(), values.password, geolocation);
      setAccountLocked(false);
      toast.success('로그인 성공!');

      const nextUser = useAuthStore.getState().user;
      if (nextUser?.temporaryPasswordExpired) {
        toast.warning(
          '임시 비밀번호가 만료되었습니다. 관리자에게 재발급을 요청해주세요.',
        );
        await useAuthStore.getState().logout();
        return;
      }
      // 로그인 응답 mustChangePassword — 대시보드 진입 전에 강제 변경으로 보낸다
      if (nextUser?.mustChangePassword) {
        navigate({ to: ROUTE_PATHS.FORCE_CHANGE_PASSWORD } as never);
        return;
      }
      navigate({ to: ROUTE_PATHS.DASHBOARD } as never);
    } catch (error) {
      if (isLoginAccountLockedError(error)) {
        setAccountLocked(true);
        return;
      }
      setAccountLocked(false);
      handleApiError(error, toast);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isInitialized || isLoading) {
    return (
      <div className="login-container">
        <LoadingSpinner fullscreen tip="인증 확인 중..." />
      </div>
    );
  }

  const inputTone = accountLocked ? 'error' : 'default';

  return (
    <div className="login-container">
      <Card className="login-card w-full max-w-md border-0">
        <CardContent className="p-8">
          <div className="mb-8 text-center">
            <img
              src="/rudalink_bi.svg"
              alt="RUDALINK"
              className="mx-auto h-8 w-auto max-w-[200px] object-contain"
            />
            <p className="mt-3 text-sm text-muted-foreground">
              관리자 계정으로 로그인하세요
            </p>
          </div>

          <Form {...form}>
            <Stack as="form" gap={16} onSubmit={form.handleSubmit(onSubmit)}>
              {accountLocked ? <LoginAccountLockedAlert /> : null}

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>이메일</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        autoComplete="email"
                        size="sm"
                        tone={inputTone}
                        placeholder="name@rudalink.com"
                        startIcon={<Mail />}
                        clearable={false}
                        className="w-full"
                        aria-invalid={accountLocked || undefined}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>비밀번호</FormLabel>
                    <FormControl>
                      <PasswordInput
                        {...field}
                        autoComplete="current-password"
                        size="sm"
                        tone={inputTone}
                        placeholder="비밀번호"
                        startIcon={<Lock />}
                        className="w-full"
                        aria-invalid={accountLocked || undefined}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="ruda-button w-full"
                disabled={isLoading || isSubmitting}
              >
                로그인
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                로그인 시 접속 기록 확인을 위해 브라우저 위치 정보 제공을 요청할
                수 있습니다. 허용하지 않아도 로그인할 수 있습니다.
              </p>
            </Stack>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};
